"use client";

import React from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  Tooltip,
  Spinner,
  Flex,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
// MeetingData type is likely from a local ./types, if meetings prop is removed, this might be too.
// import { MeetingData } from "./types"; 
import { useMeetingContext } from "../meeting/MeetingContext";
// ApiMeeting should be used directly for type consistency
import type { Meeting as ApiMeeting } from "../../services/meetingService";


interface MeetingHistoryProps {
  meetings?: ApiMeeting[]; // Modified: Make optional to allow fallback to context
  propClientId?: any;
  clientName?: string;
}

const MeetingHistory: React.FC<MeetingHistoryProps> = ({ meetings: propMeetings, propClientId,clientName }) => {
  const navigate = useNavigate();
  const {
    meetings: contextMeetings,
    isLoading: contextIsLoading,
    error: contextError,
    addMeetingToContext
  } = useMeetingContext();

  // Prioritize meetings passed via props, otherwise use context
  const meetingsToDisplay = propMeetings || contextMeetings;
  // If meetings are passed as a prop, we assume they are already loaded.
  // isLoading and error should then primarily reflect the context's state if propMeetings is not provided.
  const isLoading = !propMeetings && contextIsLoading;
  const error = !propMeetings ? contextError : null; // If propMeetings is present, assume no error state from context is relevant for them
  
  const filteredMeetings: ApiMeeting[] = propClientId
    ? meetingsToDisplay.filter(m => m.clientId && m.clientId==propClientId)
    : meetingsToDisplay; 
  
  const handleMeetingClick = (meeting: ApiMeeting) => {
    // Seed the clicked meeting into the shared MeetingContext before navigating.
    // The details page reads the meeting from context, so this lets it render
    // immediately instead of scanning /api/meetings page-by-page to re-find it
    // (the slow "Searching for meeting..." that could also miss the meeting
    // entirely when it wasn't in the first few pages).
    if (meeting?.meeting_id) {
      addMeetingToContext(meeting);
    }
    navigate(`/meeting-details/${meeting.meeting_id}`);
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={6} mt={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle mr={2}>Error loading meeting history!</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (filteredMeetings.length === 0) {
    return (
      <Box p={6} mt={4} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
        <Heading size="md" fontWeight="semibold" mb={4}>
          Meeting History:
        </Heading>
        <Text>{clientName ? `No meeting history found for ${clientName}.` : "No meeting history available."}</Text>
      </Box>
    );
  }
  
  return (
    <Box
      overflow="hidden"
      bg="white"
      mt={4}
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      w="full"
    >
      <Box p={6}>
        <Heading size="md" fontWeight="semibold" mb={4}>
          Meeting History:
        </Heading>
        
        <Box
          maxH="400px"
          overflowY="auto"
          overflowX="hidden"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E0',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#A0AEC0',
            },
          }}
        >
          <Table variant="simple" size="sm" w="100%">
            <Thead bg="blue.50" position="sticky" top={0} zIndex={1}>
              <Tr>
                <Th width="130px" fontWeight="medium" fontSize="xs">Date</Th>
                <Th fontWeight="medium" fontSize="xs">Duration</Th>
                <Th width="150px" fontWeight="medium" fontSize="xs">Score</Th>
                <Th width="200px" fontWeight="medium" fontSize="xs">Note/Name</Th>
                <Th width="150px" fontWeight="medium" fontSize="xs">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMeetings.map((meeting) => (
                <Tooltip 
                  key={meeting.id} // Assuming meeting.id is stable and unique
                  label="Click to view meeting details"
                  placement="top"
                  hasArrow
                >
                  <Tr 
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => handleMeetingClick(meeting)}
                    transition="background-color 0.2s"
                  >
                    <Td color="gray.600" fontSize="xs" fontWeight="semibold">
                      {meeting.date ? new Date(meeting.date).toLocaleDateString() : "N/A"}
                    </Td>
                    <Td color="gray.600" fontSize="xs" fontWeight="semibold">
                      {meeting.duration || "N/A"}
                    </Td>
                    <Td>
                      <Box py={1.5}>
                        <Progress 
                          value={meeting.scorePercentage || 0} 
                          max={100}
                          size="xs"
                          colorScheme={ (meeting.scorePercentage || 0) > 50 ? "blue" : "orange" }
                          borderRadius="full"
                          bg="gray.200"
                        />
                      </Box>
                    </Td>
                    <Td color="gray.600" fontSize="xs" fontWeight="semibold" whiteSpace="pre-wrap" wordBreak="break-word">
                      {meeting.note || meeting.name || "N/A"} {/* Display note, fallback to name */}
                    </Td>
                    <Td>
                      <Badge
                        px={2}
                        py={1}
                        borderRadius="sm"
                        fontSize="xs"
                        fontWeight="semibold"
                        colorScheme={
                          meeting.status?.toLowerCase() === "completed" ? "green" : 
                          meeting.status?.toLowerCase() === "scheduled" ? "blue" :
                          meeting.status?.toLowerCase() === "cancelled" ? "red" :
                          "gray"
                        }
                        // More dynamic bg/color could be added if needed
                      >
                        {meeting.status || "Unknown"}
                      </Badge>
                    </Td>
                  </Tr>
                </Tooltip>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

export default MeetingHistory; 